import React, { useState } from 'react';
import styled from 'styled-components';
import { grayShades } from 'styles/abstracts/colors';
import Icon from '_Components/Icons/Icon';
import NoDataMessage from '_Components/NoDataMessage/NoDataMessage';

const SpanChevron = styled.span`
  width: 1.5rem;
  height: 1.5rem;
  margin-right: 1rem;
  display: inline-block;
`;

const ChevronButton = styled(SpanChevron)`
  border-radius: 0.75rem;
  background-color: ${grayShades.g300};
  text-align: center;
  cursor: pointer;
`;

const IrritantsAndEnchantingPointsAIInsightsTable = ({ insight, t, data = [] }) => {
  const MIN_TEXT_SIZE = 300;

  const tableStyle = {
    tableContainer: {
      width: '100%',
      overflowX: 'auto',
      maxHeight: '800px', // Adjust the height as needed
      overflowY: 'scroll',
      display: 'block',
    },
    thead: {
      position: 'sticky',
      top: 0,
      backgroundColor: '#f1f1f1', // Optional: to make the header stand out
      zIndex: 1,
    },
    tbody: {
      display: 'block',
      maxHeight: '250px', // Adjust the height as needed
      overflowY: 'auto',
    },
    th: {
      border: '1px solid #FFFFFF',
      textAlign: 'center',
      padding: '8px',
      backgroundColor: insight === 'irritants' ? '#000000' : '#8bdb00',
      color: '#FFFFFF',
    },
    td: { border: '1px solid #FFFFFF', padding: '8px', overflow: 'hidden' },
  };

  const [expandedRows, setExpandedRows] = useState({});
  const [currentVerbatimIndex, setCurrentVerbatimIndex] = useState(0);

  const nextVerbatim = () => {
    let maxVerbatimSize = Math.max(...data.map(item => item.verbatims.length));
    if (maxVerbatimSize < 0) maxVerbatimSize = 0;

    const temp = currentVerbatimIndex + 1;
    if (temp >= maxVerbatimSize) {
      setCurrentVerbatimIndex(0);
    } else {
      setCurrentVerbatimIndex(temp);
    }
  };

  const previousVerbatim = () => {
    const temp = currentVerbatimIndex - 1;
    if (temp <= 0) {
      setCurrentVerbatimIndex(4);
    } else {
      setCurrentVerbatimIndex(temp);
    }
  };

  const toggleExpand = index => {
    setExpandedRows(prev => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  if (_.isEmpty(data)) {
    return <NoDataMessage mainTitle="No result" secondText="Please, select a thematic with more verbatim" />;
  }

  return (
    <div>
      <style>
        {`
        .even-row {
          background-color: white;
        }
        .odd-row {
          background-color: #f0f0f0;
        }
        .expandable-cell {
          position: relative;
          max-height: 50px; /* Set a fixed height */
          overflow: hidden;
          transition: max-height 0.3s ease;
        }
        .expandable-cell.expanded {
          max-height: 200px; /* Adjust as needed */
        }
        .ellipsis {
          color: #007bff;
          cursor: pointer;
          font-weight: bold;
        }
        .ellipsis:hover {
          text-decoration: underline;
        }
      `}
      </style>
      <table style={{ width: '100%', borderCollapse: 'collapse', ...tableStyle.tableContainer }}>
        <thead style={tableStyle.thead}>
          <tr style={tableStyle.tr}>
            <th style={tableStyle.th}>{t('card:Subtheme')}</th>
            <th style={tableStyle.th}>{insight === 'irritants' ? t('card:Irritant') : t('card:Enchantment')}</th>
            <th style={tableStyle.th}>{t('card:Explications')}</th>
            <th style={tableStyle.th}>{t('card:Frequency')}</th>
            <th style={tableStyle.th}>
              <ChevronButton onClick={() => previousVerbatim()}>
                <Icon icon="INTERFACE_CHEVRON_LEFT" color={grayShades.g800} />
              </ChevronButton>
              <span style={{ marginRight: '1em' }}>{t('card:Verbatims')}</span>
              <ChevronButton onClick={() => nextVerbatim()}>
                <Icon icon="INTERFACE_CHEVRON_RIGHT" color={grayShades.g800} />
              </ChevronButton>
            </th>
          </tr>
        </thead>
        <tbody>
          {data.map((item, index) => {
            const verbatimsSize = _.size(item.verbatims);
            const rowIndex = currentVerbatimIndex + 1 > verbatimsSize ? verbatimsSize - 1 : currentVerbatimIndex;
            const verbatimText = _.get(item.verbatims, rowIndex)?.text;

            return (
              <tr key={item.id} className={index % 2 === 0 ? 'even-row' : 'odd-row'}>
                <td style={tableStyle.td} className="expandable-cell">
                  {expandedRows[index] ? item.subThematic : `${item.subThematic.slice(0, MIN_TEXT_SIZE)}`}
                  {item.subThematic.length > MIN_TEXT_SIZE && (
                    <span className="ellipsis" onClick={() => toggleExpand(index)}>
                      {expandedRows[index] ? 'Voir moins' : '...Voir plus'}
                    </span>
                  )}
                </td>
                <td style={tableStyle.td} className="expandable-cell">
                  {expandedRows[index] ? item.title : `${item.title.slice(0, MIN_TEXT_SIZE)}`}
                  {item.title.length > MIN_TEXT_SIZE && (
                    <span className="ellipsis" onClick={() => toggleExpand(index)}>
                      {expandedRows[index] ? 'Voir moins' : '...Voir plus'}
                    </span>
                  )}
                </td>

                <td style={tableStyle.td} className="expandable-cell">
                  {expandedRows[index] ? item.explainSummary : `${item.explainSummary.slice(0, MIN_TEXT_SIZE)}`}
                  {item.explainSummary.length > MIN_TEXT_SIZE && (
                    <span className="ellipsis" onClick={() => toggleExpand(index)}>
                      {expandedRows[index] ? 'Voir moins' : '...Voir plus'}
                    </span>
                  )}
                </td>
                <td style={{ ...tableStyle.td, minWidth: '14%' }}>
                  {_.times(item.proportion).map(_i => (
                    <span>
                      <Icon icon="PICTO_STAR_BLACK" color="#592EA0" size={24} />
                    </span>
                  ))}
                  {_.times(5 - item.proportion).map(_i => (
                    <span>
                      <Icon icon="PICTO_STAR_WHITE" color="#592EA0" size={24} />
                    </span>
                  ))}
                </td>
                <td style={{ ...tableStyle.td, minWidth: '25%' }} className="expandable-cell">
                  <span>
                    <Icon icon="PICTO_PERSON" color="#592EA0" />
                    <Icon icon="PICTO_MESSAGE" color="#592EA0" size={48} />
                  </span>

                  <span>{`${rowIndex + 1} / ${verbatimsSize}`}</span>
                  <p>
                    <i>
                      {expandedRows[index] ? verbatimText : `${verbatimText?.slice(0, MIN_TEXT_SIZE)}`}
                      {verbatimText?.length > MIN_TEXT_SIZE && (
                        <span className="ellipsis" onClick={() => toggleExpand(index)}>
                          {expandedRows[index] ? 'Voir moins' : '...Voir plus'}
                        </span>
                      )}
                    </i>
                  </p>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default IrritantsAndEnchantingPointsAIInsightsTable;
